import React, { useState } from 'react';
import { Modal, Button, Form } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import Loader from './Loader';

interface CreateRoundReasonModalProps {
    show: boolean;
    onHide: () => void;
    onConfirm: (reason: string, otherText?: string) => void;
    moduleName: string;
    isLoading?: boolean;
}

const CreateRoundReasonModal: React.FC<CreateRoundReasonModalProps> = ({ show, onHide, onConfirm, moduleName, isLoading = false }) => {
    const { t } = useTranslation('components');
    const [selectedReason, setSelectedReason] = useState<string>('client_request');
    const [otherText, setOtherText] = useState<string>('');

    const reasons = [
        { value: 'client_request', label: t('Client request') },
        { value: 'initiated', label: t('Initiated') },
        { value: 'project', label: t('Project') },
        { value: 'other', label: t('Other') },
    ];

    const handleConfirm = () => {
        if (selectedReason === 'other' && !otherText.trim()) {
            alert(t('Please enter description'));
            return;
        }
        const finalReason = selectedReason === 'other' ? otherText : reasons.find((r) => r.value === selectedReason)?.label || selectedReason;
        onConfirm(finalReason, selectedReason === 'other' ? otherText : undefined);
        handleClose();
    };

    const handleClose = () => {
        if (isLoading) return; // Prevent closing during loading
        setSelectedReason('client_request');
        setOtherText('');
        onHide();
    };

    return (
        <Modal show={show} onHide={handleClose} centered dir="rtl" backdrop={isLoading ? 'static' : true}>
            <Modal.Header closeButton={!isLoading} style={{ textAlign: 'right' }}>
                <Modal.Title style={{ textAlign: 'right', width: '100%' }}>
                    {moduleName} - {t('Opening new round')}
                </Modal.Title>
            </Modal.Header>
            <Modal.Body style={{ textAlign: 'right', direction: 'rtl', position: 'relative' }}>
                {isLoading ? (
                    <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '200px' }}>
                        <Loader />
                    </div>
                ) : (
                    <Form dir="rtl">
                        <Form.Group className="mb-3" style={{ textAlign: 'right', direction: 'rtl' }}>
                            <h5 style={{ textAlign: 'right', fontWeight: '600', fontSize: '1.15rem', marginBottom: '16px', color: '#212529' }}>
                                {t('Creation reason')}
                            </h5>
                            {reasons.map((reason) => (
                                <Form.Check key={reason.value} type="radio" reverse className="mb-2">
                                    <Form.Check.Input
                                        type="radio"
                                        id={`reason-${reason.value}`}
                                        name="reason"
                                        value={reason.value}
                                        checked={selectedReason === reason.value}
                                        onChange={(e) => setSelectedReason(e.target.value)}
                                    />
                                    <Form.Check.Label htmlFor={`reason-${reason.value}`}>{reason.label}</Form.Check.Label>
                                </Form.Check>
                            ))}
                        </Form.Group>
                        {selectedReason === 'other' && (
                            <Form.Group className="mb-3" style={{ textAlign: 'right' }}>
                                <Form.Label style={{ textAlign: 'right' }}>{t('Reason description')}</Form.Label>
                                <Form.Control
                                    type="text"
                                    placeholder={t('Enter description...')}
                                    value={otherText}
                                    onChange={(e) => setOtherText(e.target.value)}
                                    dir="rtl"
                                    style={{ textAlign: 'right' }}
                                />
                            </Form.Group>
                        )}
                    </Form>
                )}
            </Modal.Body>
            <Modal.Footer style={{ direction: 'rtl', justifyContent: 'flex-start' }}>
                <Button variant="primary" onClick={handleConfirm} disabled={isLoading}>
                    {t('Confirm')}
                </Button>
                <Button variant="secondary" onClick={handleClose} disabled={isLoading} style={{ marginLeft: '10px' }}>
                    {t('Cancel')}
                </Button>
            </Modal.Footer>
        </Modal>
    );
};

export default CreateRoundReasonModal;
